import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import {
  MDBModal,
  MDBModalBody,
  MDBModalContent,
  MDBModalDialog,
  MDBModalFooter,
  MDBModalHeader,
  MDBModalTitle,
  MDBSpinner,
  MDBChart,
  MDBCol,
  MDBRow,
  MDBBtn,
  MDBIcon,
  MDBDropdown,
  MDBDropdownMenu,
  MDBDropdownToggle,
  MDBDropdownItem,
} from "mdb-react-ui-kit";
import jsPDF from "jspdf";
import "jspdf-autotable";
import fetchBillsWithAmendments from "../../utils/fetchBillsWithAmendments";
import fetchBillVotesDetails from "../../utils/fetchBillVotesDetails";
import fetchBillDetailsWithBillId from "../../utils/fetchBillDetailsWithBillId";

export const formatVoteText = (vote) => {
  switch (vote) {
    case "YES_BY_PROXY":
      return "Yes *";
    case "NO_BY_PROXY":
      return "No *";
    case "YES":
      return "Yes";
    case "NO":
      return "No";
    case "ABSENT":
      return "Absent";
    case "ABSTAINED":
      return "Abstain/No Vote";
    case "NO_VOTE":
      return "Abstain/No Vote";
    default:
      return vote || "N/A";
  }
};

export const formatFloorVoteText = (vote) => {
  switch (vote) {
    case "YES":
      return "Yes";
    case "NO":
      return "No";
    case "ABSENT":
      return "Absent";
    case "EXCUSED":
      return "Excused";
    case "YES_EXCUSED":
      return "Yes *";
    case "NO_EXCUSED":
      return "No *";
    default:
      return vote || "N/A";
  }
};

const getVoteDate = (vote) => {
  return new Date(vote.voteTime || vote.dateTime || "");
};

function VoteHistory({ basicModal, setBasicModal, billId }) {
  const [loading, setLoading] = useState(false);
  const [voteData, setVoteData] = useState([]);
  const [currentVoteData, setCurrentVoteData] = useState(null);
  const [voteDataRaw, setVoteDataDataRaw] = useState([]);
  const [legislatorMap, setLegislatorMap] = useState({});
  const [selectedVote, setSelectedVote] = useState(null);
  const [amendmentMap, setAmendmentMap] = useState({});
  const [billNumber, setBillNumber] = useState(null);

  const toggleOpen = () => setBasicModal(!basicModal);
  const voteColumnBody = (rowData) => {
    const voteVal = rowData.committeeVote || rowData.voteType;
    const isFloorVote = !rowData.committeeVote;
  
    return isFloorVote
      ? formatFloorVoteText(voteVal)
      : formatVoteText(voteVal);
  };

  useEffect(() => {
    if (basicModal) {
      fetchVoteData();
    }
  }, [basicModal]);

  useEffect(() => {
    if (basicModal && billId) {
      const isHB2 = billNumber?.trim() === "HB 2";

      // Pass a minimal bill object array with the billId
      fetchBillsWithAmendments([{ id: billId }])
        .then((billsWithAmendments) => {
          if (billsWithAmendments.length > 0) {      
            const amendments = billsWithAmendments[0].amendments || [];
            const map = {};
            amendments.forEach((amendment) => {
              // Format the billNumber to remove any space and insert zero(s) between the bill type and number, e.g., "HB 178" becomes "HB0178".
              const [billType, billNumRaw] = billNumber?.split(" ") || [];
              const paddedBillNumber = String(billNumRaw).padStart(4, "0");
              const formattedBillNumber = `${billType}${paddedBillNumber}`; // e.g. HB0002
            
              const versionPart = String(amendment.billVersion).padStart(3, '0');
            
              // Only apply section logic if this is the special HB 2
              const sectionPart = isHB2 && amendment.section ? `.001.${amendment.section}` : "";
            
              const numberPart = String(amendment.number).padStart(3, '0');
            
              const constructedName = `${formattedBillNumber}.${versionPart}${sectionPart}.${numberPart}`;
              map[Number(amendment.id)] = constructedName;
            });
            
            setAmendmentMap(map);
          }
        })
        .catch((err) =>
          console.error("Error fetching amendments for bill", billId, err)
        );
        fetchBillDetailsWithBillId(billId).then((billDetails) => {
          setBillNumber(`${billDetails.billType.code} ${billDetails.billNumber}`);
        });
    }
  }, [basicModal, billId, billNumber]);

  useEffect(() => {
    if (!voteDataRaw.length) return;
  
    const updatedWithLabels = voteDataRaw.map((vote) => {
      return {
        ...vote,
        amendmentLabel: vote.standingCommitteeMeeting
          ? (vote.amendmentId && amendmentMap[Number(vote.amendmentId)]) || ""
          : getCustomAmendmentNumberFromMotion(vote.motion),
      };
    });
  
    setVoteData(updatedWithLabels);
  }, [amendmentMap, voteDataRaw]);

  const fetchVoteData = async () => {
    setLoading(true);
    try {
      const { executiveActionData, floorVoteData } = await fetchBillVotesDetails(billId);  
      const allVotes = [...(executiveActionData || []), ...(floorVoteData || [])];
      
      const isHB2 = (vote) =>
        vote.bill?.billType?.code === "HB" && String(vote.bill?.billNumber) === "2";

      // Filter out ones that don't have legislatorVotes (just in case)
      const voteData = allVotes.filter(vote => Array.isArray(vote.legislatorVotes));
  
      // Extract Unique Legislator IDs
      const legislatorIds = [
        ...new Set(
          voteData.flatMap((vote) =>
            vote.legislatorVotes.map((v) =>
              v.membership?.legislatorId || v.legislatorId
            )
          )
        ),
      ];
  
      const legislatorResponse = await axios.post(
        `${process.env.REACT_APP_LAWS_BACKEND_HOST}/legislators/v1/legislators/search?limit=1000&offset=0`,
        { legislatorIds }
      );
  
      const fetchedLegislators = legislatorResponse.data?.content ?? [];
  
      const newLegislatorMap = fetchedLegislators.reduce((acc, legislator) => {
        acc[legislator.id] = {
          firstName: legislator.firstName || "",
          lastName: legislator.lastName || "",
          party: legislator.politicalParty?.name || "Unknown",
        };
        return acc;
      }, {});
      setLegislatorMap(newLegislatorMap);
  
      const membershipOrder = { Chair: 0, "Vice Chair": 1, Member: 2 };
  
      const updatedVoteData = voteData.map((vote) => {
        const rawYesVotes = vote.legislatorVotes.filter((v) => {
          const voteVal = v.committeeVote || v.voteType;
          return voteVal?.includes("YES");
        }).length;
        
        const rawNoVotes = vote.legislatorVotes.filter((v) => {
          const voteVal = v.committeeVote || v.voteType;
          return voteVal?.includes("NO");
        }).length;

        // HB 2 is Special
        const isSpecialHB2 = isHB2(vote);
  
        return {
          ...vote,
          rawYesVotes,
          rawNoVotes,
          voteDate: getVoteDate(vote),
          type: vote.standingCommitteeMeeting ? "Committee" : "Floor",
          committeeLabel: getCommitteeLabel(vote),
          motionLabel: isSpecialHB2
            ? vote.motion || "N/A"
            : getMotionLabel(vote),
          amendmentLabel:
            (vote.amendmentId && amendmentMap[Number(vote.amendmentId)]) || "",
          legislatorVotes: vote.legislatorVotes
            .map((voteItem) => {
              const legislatorId = voteItem.membership?.legislatorId || voteItem.legislatorId;
              const legislator = newLegislatorMap[legislatorId] || {};
              const membershipType = voteItem.membership?.type?.name || "Member";
              const sortOrder = membershipOrder[membershipType] ?? 3;
            
              return {
                ...voteItem,
                rawParty: legislator.party || "Unknown",
                legislatorName: `${legislator.lastName || "Unknown"}, ${legislator.firstName || ""}`,
                membershipName: membershipType,
                legislatorId,
                sortOrder,
              };
            })
            .sort((a, b) => {
              if (a.sortOrder !== b.sortOrder) {
                return a.sortOrder - b.sortOrder;
              }
              return a.legislatorName.localeCompare(b.legislatorName);
            }),
        };
      });
  
      const sortedVoteData = updatedVoteData.sort(
        (a, b) => new Date(b.voteTime) - new Date(a.voteTime)
      );
  
      setVoteDataDataRaw(sortedVoteData);
      if (sortedVoteData.length > 0) {
        setCurrentVoteData(sortedVoteData[0]);
      }
    } catch (error) {
      console.error("Error fetching vote data:", error);
    }
    setLoading(false);
  };

  // Helper Function to Parse Custom Amendment Number
  const getCustomAmendmentNumberFromMotion = (motion) => {
    if (!motion) return "";
    const match = motion.match(/AMD-(HB\d{4}\.\d{3}\.\d{3}(?:\.[A-Z]\.\d{3})?)/);
    return match ? match[1] : "";
  };
  
  const handleRowClick = (vote) => {
    setCurrentVoteData(vote);
    setSelectedVote(vote);
  };

  const yesVotes = useMemo(() => {
    return currentVoteData
      ? currentVoteData.legislatorVotes.filter((v) => {
          const voteVal = v.committeeVote || v.voteType;
          return voteVal === "YES" || voteVal === "YES_BY_PROXY";
        }).length
      : 0;
  }, [currentVoteData]);
  
  const noVotes = useMemo(() => {
    return currentVoteData
      ? currentVoteData.legislatorVotes.filter((v) => {
          const voteVal = v.committeeVote || v.voteType;
          return voteVal === "NO" || voteVal === "NO_BY_PROXY";
        }).length
      : 0;
  }, [currentVoteData]);

  const votesByParty = useMemo(() => {
    const partyVotes = {};
    if (currentVoteData) {
      currentVoteData.legislatorVotes.forEach((vote) => {
        const legislatorId = vote.membership?.legislatorId || vote.legislatorId;
        const party = legislatorMap[legislatorId]?.party || "Unknown";
        if (!partyVotes[party]) {
          partyVotes[party] = { yes: 0, no: 0 };
        }
        const voteVal = vote.committeeVote || vote.voteType;
        if (voteVal?.includes("YES")) {
          partyVotes[party].yes += 1;
        } else if (voteVal?.includes("NO")) {
          partyVotes[party].no += 1;
        }
      });
    }
    return partyVotes;
  }, [currentVoteData]);

  const partyLabels = Object.keys(votesByParty);
  const yesData = partyLabels.map((party) => votesByParty[party].yes);
  const noData = partyLabels.map((party) => votesByParty[party].no);

  const formatMotionText = (motion) => {
    if (!motion) return "N/A";
    return motion
      .split("_") // Split by underscore
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()) // Capitalize first letter
      .join(" "); // Join words back with space
  };

  const printVotesPDF = async (mode = "Selected") => {
    const formatMotionText = (motion) => {
      if (!motion) return "N/A";
      return motion
        .split("_")
        .map(
          (word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
        )
        .join(" ");
    };

    const doc = new jsPDF({
      orientation: "portrait",
      unit: "pt",
      format: "a4",
    });

    const margin = 36;
    const pageWidth = doc.internal.pageSize.width;
    const logoPath = "/logo192.png";

    const convertToBase64 = async (path) => {
      return new Promise((resolve, reject) => {
        const img = new Image();
        img.src = path;
        img.onload = () => {
          const canvas = document.createElement("canvas");
          canvas.width = img.width;
          canvas.height = img.height;
          const ctx = canvas.getContext("2d");
          ctx.drawImage(img, 0, 0);
          resolve(canvas.toDataURL("image/png"));
        };
        img.onerror = reject;
      });
    };

    const logoBase64 = await convertToBase64(logoPath);
    const logoWidth = 40;
    const logoHeight = 40;

    doc.addImage(logoBase64, "PNG", margin, 20, logoWidth, logoHeight);

    doc.setFontSize(12);
    doc.text("69th Legislature 2025", pageWidth / 2, margin, {
      align: "center",
    });
    doc.setFontSize(14);
    doc.text(`${billNumber} Vote History`, pageWidth / 2, margin + 20, {
      align: "center",
    });

    const sortedVoteData = [...voteData].sort(
      (a, b) => new Date(a.voteTime) - new Date(b.voteTime)
    );

    const getVoteDate = (vote) => {
      if (!vote) return new Date("");
      return new Date(vote.voteTime || vote.dateTime || "");
    };
    
    const getCommitteeLabel = (vote) => {
      return vote.standingCommitteeMeeting?.standingCommittee?.committeeDetails?.committeeCode?.name ||
        (vote.systemId?.chamber === "HOUSE" ? "(H) Committee of the Whole" :
         vote.systemId?.chamber === "SENATE" ? "(S) Committee of the Whole" :
         "N/A");
    };
    
    const getMotionLabel = (vote) => {
      return vote.standingCommitteeMeeting?.standingCommittee
        ? formatMotionText(vote.motion)
        : vote.billStatus?.billStatusCode?.name || "N/A";
    };

    // First Table - Summary of All Votes
    const summaryTable = ["Date", "Committee", "Motion", "Amendment", "Yes", "No"];
    const summaryRows = sortedVoteData.map((vote) => [
      getVoteDate(vote).toLocaleDateString(),
      getCommitteeLabel(vote),
      vote.standingCommitteeMeeting?.standingCommittee
        ? formatMotionText(vote.motion)
        : vote.billStatus?.billStatusCode?.name || "N/A",
      vote.standingCommitteeMeeting
        ? (vote.amendmentId && amendmentMap[Number(vote.amendmentId)]) || ""
        : getCustomAmendmentNumberFromMotion(vote.motion),
      vote.rawYesVotes || 0,
      vote.rawNoVotes || 0
    ]);

    doc.autoTable({
      head: [summaryTable],
      body: summaryRows,
      startY: margin + 40,
      margin: { left: margin, right: margin },
      headStyles: { fillColor: [103, 137, 127] },
      alternateRowStyles: { fillColor: [240, 240, 240] },
      styles: { fontSize: 8 },
    });

    let nextTableY = doc.autoTable.previous.finalY + 20;

    // Print details for the selected vote
    if (mode === "Selected" && currentVoteData) {
      const amendmentLabel =
      currentVoteData?.amendmentId &&
      amendmentMap[Number(currentVoteData.amendmentId)]
        ? `, ${amendmentMap[Number(currentVoteData.amendmentId)]}`
        : "";

      doc.setFontSize(8);
      doc.text(
      `Details: ${getVoteDate(currentVoteData).toLocaleDateString()}, ${getCommitteeLabel(currentVoteData)}, ${getMotionLabel(currentVoteData)}${amendmentLabel}`,
        margin,
        nextTableY
      );

      nextTableY += 5;

      const detailTable = ["Legislator", "Party", "Vote"];
      const detailRows = currentVoteData.legislatorVotes
        .map((vote) => [
          vote.legislatorName || "N/A",
          vote.rawParty || "Unknown",
          formatVoteText(vote.committeeVote || vote.voteType),
        ])
        .sort((a, b) => a[0].localeCompare(b[0]));

      doc.autoTable({
        head: [detailTable],
        body: detailRows,
        startY: nextTableY + 6,
        margin: { left: margin, right: margin },
        headStyles: { fillColor: [103, 137, 127] },
        alternateRowStyles: { fillColor: [240, 240, 240] },
        styles: { fontSize: 8 },
      });

      nextTableY = doc.autoTable.previous.finalY + 20;
    }

    // Print all vote details when "Print All" is selected
    if (mode === "All") {
      sortedVoteData.forEach((vote) => {
        const amendmentLabel = vote.standingCommitteeMeeting
          ? (vote.amendmentId && amendmentMap[Number(vote.amendmentId)]) || ""
          : getCustomAmendmentNumberFromMotion(vote.motion);

        const thisAmendmentLabel = amendmentLabel ? `, ${amendmentLabel}` : "";

        doc.setFontSize(8);
        doc.text(
        `Details: ${getVoteDate(vote).toLocaleDateString()}, ${getCommitteeLabel(vote)}, ${getMotionLabel(vote)}${thisAmendmentLabel}`,
          margin,
          nextTableY
        );

        nextTableY += 5;

        const detailTable = ["Legislator", "Party", "Vote"];
        const detailRows = vote.legislatorVotes
          .map((vote) => [
            vote.legislatorName || "N/A",
            vote.rawParty || "Unknown",
            formatVoteText(vote.committeeVote || vote.voteType),
          ])
          .sort((a, b) => a[0].localeCompare(b[0]));

        doc.autoTable({
          head: [detailTable],
          body: detailRows,
          startY: nextTableY + 6,
          margin: { left: margin, right: margin },
          headStyles: { fillColor: [103, 137, 127] },
          alternateRowStyles: { fillColor: [240, 240, 240] },
          styles: { fontSize: 8 },
        });

        nextTableY = doc.autoTable.previous.finalY + 20;
      });
    }

    const pageCount = doc.internal.getNumberOfPages();
    const printedOn = new Date().toLocaleString([], {
      year: "numeric",
      month: "numeric",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });

    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.setFontSize(8);
      doc.setTextColor(100);

      // Date printed
      doc.text(`Printed: ${printedOn}`, margin, doc.internal.pageSize.height - 20);

      // Page X of Y
      const pageLabel = `Page ${i} of ${pageCount}`;
      doc.text(pageLabel, pageWidth - margin - doc.getTextWidth(pageLabel), doc.internal.pageSize.height - 20);
    }

    doc.save(`Vote-History-${billNumber}-${mode}.pdf`);
  };

  const getCommitteeLabel = (vote) => {
    return vote.standingCommitteeMeeting?.standingCommittee?.committeeDetails?.committeeCode?.name ||
      (vote.systemId?.chamber === "HOUSE" ? "(H) Committee of the Whole" :
       vote.systemId?.chamber === "SENATE" ? "(S) Committee of the Whole" :
       "N/A");
  };

  const getMotionLabel = (vote) => {
    return vote.standingCommitteeMeeting?.standingCommittee
      ? formatMotionText(vote.motion)
      : vote.billStatus?.billStatusCode?.name || "N/A";
  };

  return (
    <MDBModal
      open={basicModal}
      onClose={() => setBasicModal(false)}
      tabIndex="-1"
    >
      <MDBModalDialog size="xl">
        <MDBModalContent>
          <MDBModalHeader>
            <MDBModalTitle className="w-100">
              Vote History: {billNumber} 
            </MDBModalTitle>
            <MDBDropdown>
              <MDBDropdownToggle>
                <i className="fas fa-cloud-download-alt" aria-hidden="true"></i>{" "}
                Export
              </MDBDropdownToggle>
              <MDBDropdownMenu>
                <MDBDropdownItem
                  link
                  onClick={(e) => {
                    e.preventDefault();
                    printVotesPDF("All");
                  }}
                >
                  All Detail
                </MDBDropdownItem>
                <MDBDropdownItem
                  link
                  onClick={(e) => {
                    e.preventDefault();
                    printVotesPDF("Selected");
                  }}
                >
                  Selected or Most Recent
                </MDBDropdownItem>
                <MDBDropdownItem
                  link
                  onClick={(e) => {
                    e.preventDefault();
                    printVotesPDF("Summary");
                  }}
                >
                  Summary Only
                </MDBDropdownItem>
              </MDBDropdownMenu>
            </MDBDropdown>
            <MDBBtn
              className="btn-close"
              color="none"
              onClick={toggleOpen}
            ></MDBBtn>
          </MDBModalHeader>
          <MDBModalBody>
            {loading ? (
              <div className="text-center">
                <MDBSpinner color="primary" role="status">
                  <span className="visually-hidden">Loading Summary...</span>
                </MDBSpinner>
              </div>
            ) : (
              <>
                <h5 className="sub-heading text-center">Summary</h5>
                <DataTable
                  value={voteData.filter(vote => (vote.rawYesVotes || 0) + (vote.rawNoVotes || 0) > 0)}
                  selectionMode="single"
                  selection={selectedVote}
                  onSelectionChange={(e) => handleRowClick(e.value)}
                  stripedRows
                  resizableColumns
                  sortField="voteDate"
                  sortOrder={1}
                  removableSort
                  loading={loading}
                >
                  <Column
                    field="voteDate"
                    header="Date"
                    body={(rowData) => rowData.voteDate?.toLocaleDateString() ?? ""}
                    sortable
                  />
                  <Column
                    field="type"
                    header="Type"
                    sortable
                  />
                  <Column
                    field="committeeLabel"
                    header="Committee"
                    body={(rowData) => rowData.committeeLabel}
                    sortable
                  />
                  <Column
                    field="motionLabel"
                    header="Motion"  
                    body={(rowData) => rowData.motionLabel}
                    sortable
                  />
                  <Column
                    field="amendmentLabel"
                    header="Amendment"
                    sortable
                  />
                  <Column
                    field="rawYesVotes"
                    header="Yes"
                    body={(rowData) => rowData.rawYesVotes}
                    sortable
                    sortField="rawYesVotes"
                  />
                  <Column
                    field="rawNoVotes"
                    header="No"
                    body={(rowData) => rowData.rawNoVotes}
                    sortable
                    sortField="rawNoVotes"
                  />
                  <Column
                    header="Details"
                    body={(rowData) => (
                      <MDBBtn
                        tag="a"
                        color="none"
                        onClick={() => handleRowClick(rowData)}
                        title="View Details"
                      >
                        <MDBIcon far icon="eye" />
                      </MDBBtn>
                    )}
                    bodyStyle={{ textAlign: "center" }}
                    headerClassName="text-center"
                  />
                </DataTable>
                {selectedVote && currentVoteData && (
                  <>
                    <h5 className="mt-3 sub-heading text-center">Details</h5>
                    <p className="text-center">
                      {getVoteDate(currentVoteData).toLocaleDateString()}{" "}
                      | <strong>Committee:</strong> {getCommitteeLabel(currentVoteData)}
                      | <strong>Motion:</strong> {getMotionLabel(currentVoteData)}
                    </p>
                    <MDBRow>
                      <MDBCol md="8">
                        <DataTable
                          value={currentVoteData.legislatorVotes}
                          showGridlines
                          stripedRows
                          resizableColumns
                          sortField="sortOrder"
                          sortOrder={1}
                          removableSort
                          loading={loading}
                        >
                          <Column
                            field="legislatorName"
                            header="Legislator"
                            body={(rowData) => {
                              //const legislatorId = rowData.legislatorId || "Unknown";
                              const formattedName = rowData.legislatorName;
                              const membership = rowData.membershipName;
                              return membership && membership !== "Member"
                                ? `${formattedName} (${membership})`
                                : `${formattedName}`;
                            }}
                            sortable
                            sortField="legislatorName"
                          />
                          <Column
                            field="rawParty"
                            header="Party"
                            body={(rowData) => rowData.rawParty}
                            sortable
                          />
                          <Column
                            field="committeeVote"
                            header="Vote"
                            body={voteColumnBody}
                            bodyStyle={{ textAlign: "center" }}
                            headerClassName="text-center"
                            sortable
                          />
                        </DataTable>
                        <p className="text-center">
                          <small>
                            <strong>*</strong> = By Proxy
                          </small>
                        </p>
                      </MDBCol>
                      <MDBCol md="4">
                        {/* Determine if the vote Passed or Failed */}
                        <h6 className="text-center">
                          {yesVotes > noVotes ? "Passed" : "Failed"}
                        </h6>
                        <MDBChart
                          type="pie"
                          data={{
                            labels: ["Yes", "No"],
                            datasets: [
                              {
                                label: "Votes",
                                data: [yesVotes, noVotes],
                                backgroundColor: ["#bccdc8", "#c5afb4"],
                              },
                            ],
                          }}
                        />
                        <h6 className="mt-3 text-center">By Party</h6>
                        <MDBChart
                          type="bar"
                          data={{
                            labels: partyLabels,
                            datasets: [
                              {
                                label: "Yes",
                                data: yesData,
                                backgroundColor: "#bccdc8",
                              },
                              {
                                label: "No",
                                data: noData,
                                backgroundColor: "#c5afb4",
                              },
                            ],
                          }}
                        />
                      </MDBCol>
                    </MDBRow>
                  </>
                )}
              </>
            )}
          </MDBModalBody>
          <MDBModalFooter>
            <MDBBtn color="dark" onClick={toggleOpen}>
              Close
            </MDBBtn>
          </MDBModalFooter>
        </MDBModalContent>
      </MDBModalDialog>
    </MDBModal>
  );
}

export default VoteHistory;
